import { useMemo } from "react";
import {
  Box,
  Button,
  Icon,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
} from "@salestrak/ui";

import type { FilterConfig, FilterValue } from "#app/lib/filters/types";
import { noop } from "#app/lib/utils/noop";
import { FilterValueInput } from "./filter-value-input";
import type { Omittable } from "@salestrak/ui";

export const LABEL_COL_WIDTH = 88;

type FilterRowProps = Omittable<{
  config: FilterConfig;
  filter: FilterValue;
  onUpdate: (update: Partial<FilterValue>) => void;
  onRemove: () => void;
  leftLabel?: React.ReactNode;
}>;

/**
 * A single filter row: Field select -> Operator select -> Value input.
 *
 * When the field changes, auto-selects the first operator and clears the value.
 * When the operator is a no-value operator (isNull, isEmpty, etc.), hides the
 * value input entirely.
 */
export function FilterRow({
  config,
  filter,
  onUpdate,
  onRemove,
  leftLabel,
}: FilterRowProps) {
  const fieldOptions = useMemo(
    () =>
      Object.values(config.fields).map((f) => ({
        value: f.field,
        label: f.label,
      })),
    [config.fields]
  );

  const selectedField = filter.field ? config.fields[filter.field] : null;

  const operatorOptions = useMemo(
    () =>
      selectedField?.operators.map((op) => ({
        value: op.value,
        label: op.label,
      })) ?? [],
    [selectedField]
  );

  const selectedOperator = selectedField?.operators.find(
    (op) => op.value === filter.operator
  );

  const showValueInput = selectedOperator && selectedOperator.input !== "none";

  return (
    <Box gap="sm">
      <Box direction="row" align="center" gap="sm">
        <Box width={LABEL_COL_WIDTH} />
        <Box fill="flex">
          {/* Base UI's Select is value-based; `items` is what lets SelectValue
              render the option LABEL for the selected value. */}
          <Select
            items={fieldOptions}
            value={filter.field ?? null}
            onValueChange={(next: string | null) => {
              if (!next) return;
              const fieldDef = config.fields[next];
              const firstOp = fieldDef?.operators[0];
              onUpdate({
                field: next,
                operator: firstOp?.value ?? "eq",
                value: null,
              });
            }}
          >
            <SelectTrigger size="sm">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectPopup>
              {fieldOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  <Text>{opt.label}</Text>
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        </Box>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onRemove}
          aria-label="Remove filter"
        >
          <Icon name="trash-2" size="lg" color="muted" />
        </Button>
      </Box>

      {/* Box carries no opacity or pointer-events props, so the disabled wash
          is a class pair. */}
      <Box
        gap="sm"
        className={!selectedField ? "pointer-events-none opacity-40" : undefined}
      >
        <Box direction="row" align="center" gap="sm">
          <Box width={LABEL_COL_WIDTH} justify="center" align="center">
            {leftLabel ?? (
              <Text size="sm" variant="muted" weight="medium">
                Where
              </Text>
            )}
          </Box>
          <Box fill="flex">
            <Select
              items={operatorOptions}
              value={filter.operator ?? null}
              onValueChange={(next: string | null) => {
                if (!next) return;
                onUpdate({
                  operator: next as FilterValue["operator"],
                  value: null,
                });
              }}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="Is" />
              </SelectTrigger>
              <SelectPopup>
                {operatorOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <Text>{opt.label}</Text>
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </Box>
        </Box>

        <Box direction="row" align="center" gap="sm">
          <Box width={LABEL_COL_WIDTH} />
          <Box fill="flex">
            {showValueInput ? (
              <FilterValueInput
                fieldType={selectedField!.type}
                value={filter.value}
                onChange={(value) => onUpdate({ value })}
                multiple={selectedOperator?.input === "multiple"}
                variant="advanced"
              />
            ) : (
              <Select value={null} onValueChange={noop} disabled>
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Selection" />
                </SelectTrigger>
                <SelectPopup />
              </Select>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
